// src/pages/JoinPage.jsx
import { useState } from "react";
import { FaDiscord, FaCheckCircle } from "react-icons/fa";

const FORMSPREE_ID = "mwvnzykn";

function JoinPage() {
  const [formData, setFormData] = useState({
    fullName: "",
    gamertag: "",
    age: "",
    country: "",
    platform: "",
    division: "",
    tiktok: "",
    twitch: "",
    youtube: "",
    instagram: "",
    clipLink: "",
    message: "",
    _gotcha: "", // honeypot — bots fill this, humans never see it
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    // Honeypot check — if filled, silently succeed (it's a bot)
    if (formData._gotcha) {
      setSubmitted(true);
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          "Full Name": formData.fullName,
          "Gamertag": formData.gamertag,
          "Age": formData.age,
          "Country / Island": formData.country,
          "Platform": formData.platform,
          "Division Interest": formData.division,
          "TikTok": formData.tiktok || "N/A",
          "Twitch": formData.twitch || "N/A",
          "YouTube": formData.youtube || "N/A",
          "Instagram": formData.instagram || "N/A",
          "Clip / Highlight Link": formData.clipLink || "N/A",
          "Why I want to join": formData.message,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError("Something went wrong. Please try again or reach out on Discord.");
      }
    } catch {
      setError("Something went wrong. Please try again or reach out on Discord.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 to-black"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight text-white mb-6">
            Join <span className="text-red-600">Prime Valor</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Think you have what it takes? Fill out the application below and a member of our team will be in touch.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-8 pb-24 px-6">
        <div className="max-w-3xl mx-auto">

          {/* Success State */}
          {submitted ? (
            <div className="bg-gray-900 rounded-lg p-12 border border-gray-800 text-center">
              <FaCheckCircle className="text-red-600 text-6xl mx-auto mb-6" />
              <h2 className="text-3xl font-black uppercase text-white mb-4">Application Received!</h2>
              <p className="text-gray-300 text-lg mb-8">
                Thanks for applying to Prime Valor. We'll review your application and get back to you soon.
              </p>
              <a
                href="https://discord.gg/SGXXCMpCxf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold uppercase tracking-wide rounded transition-colors"
              >
                <FaDiscord className="text-xl" />
                Join Our Discord
              </a>
            </div>
          ) : (
            <div className="bg-gray-900 rounded-lg p-8 border border-gray-800">
              <h2 className="text-2xl font-bold text-white mb-8 uppercase border-l-4 border-red-600 pl-4">
                Team Application
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">

                {/* Honeypot — hidden from humans, bots fill it */}
                <input
                  type="text"
                  name="_gotcha"
                  value={formData._gotcha}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                  style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
                />

                {/* Personal Info */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-red-500 mb-4">Personal Info</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        placeholder="Your real name"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Gamertag *</label>
                      <input
                        type="text"
                        name="gamertag"
                        value={formData.gamertag}
                        onChange={handleChange}
                        required
                        placeholder="Your in-game name"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Age *</label>
                      <input
                        type="number"
                        name="age"
                        value={formData.age}
                        onChange={handleChange}
                        required
                        min="13"
                        max="99"
                        placeholder="Your age"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Country / Island *</label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        placeholder="e.g. Jamaica, Trinidad..."
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Gaming Info */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-red-500 mb-4">Gaming Info</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Platform *</label>
                      <select
                        name="platform"
                        value={formData.platform}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-600 transition-colors"
                      >
                        <option value="">Select platform</option>
                        <option value="PC">PC</option>
                        <option value="PlayStation">PlayStation</option>
                        <option value="Xbox">Xbox</option>
                        <option value="Cross-platform">Cross-platform</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Division / Role *</label>
                      <select
                        name="division"
                        value={formData.division}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-600 transition-colors"
                      >
                        <option value="">Select a division</option>
                        <option value="CDL Competitive">CDL Competitive</option>
                        <option value="Warzone">Warzone</option>
                        <option value="Female Division">Female Division</option>
                        <option value="U18 Division">U18 Division</option>
                        <option value="Content Creator">Content Creator</option>
                        <option value="Editor">Editor</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Socials */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-red-500 mb-4">
                    Socials <span className="text-gray-500 font-normal normal-case tracking-normal">(optional — add what you have)</span>
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">TikTok</label>
                      <input
                        type="text"
                        name="tiktok"
                        value={formData.tiktok}
                        onChange={handleChange}
                        placeholder="@yourusername"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Twitch</label>
                      <input
                        type="text"
                        name="twitch"
                        value={formData.twitch}
                        onChange={handleChange}
                        placeholder="twitch.tv/yourusername"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">YouTube</label>
                      <input
                        type="text"
                        name="youtube"
                        value={formData.youtube}
                        onChange={handleChange}
                        placeholder="youtube.com/@yourusername"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Instagram</label>
                      <input
                        type="text"
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleChange}
                        placeholder="@yourusername"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Clip Link */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Highlight / Clip Link <span className="text-gray-600">(optional)</span>
                  </label>
                  <input
                    type="url"
                    name="clipLink"
                    value={formData.clipLink}
                    onChange={handleChange}
                    placeholder="YouTube, TikTok, Twitch clip URL..."
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Why do you want to join Prime Valor? *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Tell us about yourself, your experience, and why you want to be part of the team..."
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors resize-none"
                  ></textarea>
                </div>

                {/* Error */}
                {error && (
                  <p className="text-red-500 text-sm">{error}</p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-8 py-4 bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wide rounded-lg transition-colors"
                >
                  {submitting ? "Submitting..." : "Submit Application"}
                </button>

                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-gray-800"></div>
                  <span className="text-gray-600 text-sm">or</span>
                  <div className="flex-1 h-px bg-gray-800"></div>
                </div>

                <a
                  href="https://discord.gg/SGXXCMpCxf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold uppercase tracking-wide rounded-lg transition-colors"
                >
                  <FaDiscord className="text-xl" />
                  Join via Discord
                </a>
              </form>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export default JoinPage;
